import React, { useState, useEffect, useCallback } from 'react'
import UserProfileForm from './announceForms/announceProfileForm'
import { useTranslation } from 'react-i18next'
import MyButton from '../../components/buttons'
import * as Sentry from '@sentry/react'
import {
  postUserProfile,
  getUserProfile,
} from '../../../services/profileService'
import { IoIosWarning } from 'react-icons/io'
import Modal from '../../components/MyModal'

const FormWizard: React.FC<{ userProfileFormProps: any }> = ({
  userProfileFormProps,
}) => {
  const [formData, setFormData] = useState<{ [key: string]: any }>({})
  const [isStepValid, setIsStepValid] = useState(false)
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [dialogMessage, setDialogMessage] = useState('')
  const [dialogBgColor, setDialogBgColor] = useState('')
  const [isNewProfile, setIsNewProfile] = useState(true)
  const [showReminder, setShowReminder] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [, setIsFormModified] = useState(false)

  useEffect(() => {
    const loadFormData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const profile = await getUserProfile()
        if (profile && Object.keys(profile).length > 0) {
          setIsNewProfile(false)
          setFormData(profile)
        } else {
          setIsNewProfile(true)
          setFormData(prevData => ({
            ...prevData,
            ...userProfileFormProps,
            massageOnly: false,
          }))
        }
      } catch (err) {
        Sentry.captureException(err, {
          extra: {
            action: 'loadFormData',
            timestamp: new Date().toISOString(),
          },
          tags: {
            type: 'api_error',
            method: 'getUserProfile',
          },
        })
        setError('Failed to load profile data')
        setIsNewProfile(true)
        setFormData(prevData => ({
          ...prevData,
          ...userProfileFormProps,
          massageOnly: false,
        }))
      } finally {
        setIsLoading(false)
      }
    }

    loadFormData()
  }, [userProfileFormProps])

  const validateForm = useCallback(() => {
    if (!formData) return false

    const requiredFields: (keyof typeof formData)[] = [
      'gender',
      'orientation',
      'nationality',
      'ethnicity',
      'size',
      'weight',
      'languages',
      'consentConditions',
    ]

    const validationResults = requiredFields.map(field => {
      const value = formData[field as keyof typeof formData]
      let isValid = false

      if (field === 'languages') {
        isValid = Array.isArray(value) && value.length > 0
      } else if (field === 'size') {
        isValid = value >= 140 && value <= 220
      } else if (field === 'weight') {
        isValid = value >= 40 && value <= 120
      } else if (field === 'consentConditions') {
        isValid = value === true
      } else {
        isValid = value !== undefined && value !== null && value !== ''
      }
      return { field, isValid }
    })

    const isValid = validationResults.every(result => result.isValid)
    setIsStepValid(isValid)
    return isValid
  }, [formData])

  useEffect(() => {
    validateForm()
  }, [formData, validateForm])

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prevData => {
      const newFormData = {
        ...prevData,
        [field]: value,
      }
      localStorage.setItem('myProfile', JSON.stringify(newFormData))
      setIsFormModified(true)
      setShowReminder(true)
      return newFormData
    })
  }

  const handleSubmit = async () => {
    try {
      const dataToSend = {
        ...formData,
      }
      const response = await postUserProfile(dataToSend, isNewProfile)
      setDialogMessage(
        isNewProfile
          ? t('create-ad.profile-posted-successfully')
          : t('create-ad.profile-updated-successfully'),
      )
      setDialogBgColor('#D9FFDD')
      setIsNewProfile(false)
      setIsFormModified(false)
      setShowReminder(false)
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          action: 'handleSubmit',
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'api_error',
          method: 'postUserProfile',
        },
      })
      setDialogMessage(
        error instanceof Error
          ? error.message
          : t('create-ad.error-posting-profile'),
      )
      setDialogBgColor('#FFD9E6')
    } finally {
      setOpen(true)
    }
  }

  if (isLoading) {
    return <div>{t('page-loading')}</div>
  }

  if (error) {
    return <div>Error: {error}</div>
  }

  return (
    <div className=' w-full'>
      <UserProfileForm
        formData={formData}
        handleFieldChange={handleFieldChange}
        isNewProfile={isNewProfile}
      />

      {showReminder && (
        <div className='mt-2 flex flex-row justify-center items-center bg-redlight p-2 sm:p-4 text-xs sm:text-sm rounded-md text-center gap-2 sm:gap-3'>
          <IoIosWarning className='text-redpink text-lg sm:text-xl flex-shrink-0' />
          <span>{t('create-ad.reminder-save-changes')}</span>
        </div>
      )}

      <div className='mt-4 sm:mt-6'>
        <div className='flex justify-end'>
          <MyButton
            variant={isStepValid ? 'redpink-anthracite' : 'redpink'}
            disabled={!isStepValid}
            onClick={handleSubmit}
            className='w-full sm:w-auto min-w-[120px] sm:min-w-[150px]'
          >
            {isNewProfile ? t('create-profile') : t('update-profile')}
          </MyButton>
        </div>
      </div>

      <Modal isOpen={open} onClose={() => setOpen(false)}>
        <div className='max-w-lg w-full mx-auto'>
          <h2 className='text-lg sm:text-xl font-semibold bg-anthracite text-white p-3 sm:p-4 rounded-t-md'>
            {t('create-ad.profile-status')}
          </h2>
          <div className='p-3 sm:p-5'>
            <p
              className='rounded-md p-2 sm:p-3 text-sm sm:text-base'
              style={{ backgroundColor: dialogBgColor }}
            >
              {dialogMessage}
            </p>
            <div className='mt-4 flex justify-end'>
              <MyButton
                onClick={() => setOpen(false)}
                variant='anthracite-redpink'
                className='w-full sm:w-auto'
              >
                {t('close')}
              </MyButton>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default FormWizard
